// Shared debounced search box for tracking filter chip bars. Mirrors an
// input immediately for responsive typing while dispatching the committed
// value to the parent only after `debounceMs` of idle time — without this,
// typing "monthly" would push 7 URL updates and re-run the entire
// filter+sort+paginate pipeline per keystroke.
//
// `resetCounter` closes a race that a plain `value`-prop resync can't: the
// user types (debounce pending, value prop hasn't changed yet) then clicks
// Clear-all. If the committed value was already empty before the user
// started typing, Clear-all writes the same (already-empty) value back, so
// `value !== prevValue` never fires and the in-flight debounce survives to
// re-land the stale typed text once it fires. Callers that can reset while
// the debounce is in flight must bump `resetCounter` on every reset so the
// local draft — and the pending debounce — are force-cleared regardless of
// whether `value` visibly changed.
//
// Render-time prev-value comparison (no `useEffect`) is used to resync the
// local draft so the parent's `value`/`resetCounter` changes take effect
// synchronously within the same commit, before the pending debounce timer
// has a chance to fire.

import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";

export interface DebouncedSearchInputProps {
  /** Committed value from the parent (e.g. URL state). */
  value: string;
  /** Called with the input's value after `debounceMs` of no further typing. */
  onDebouncedChange: (next: string) => void;
  /** Bump on every parent-driven reset (e.g. Clear-all) to force the local
   *  draft to resync and cancel any in-flight debounce, even when `value`
   *  itself didn't change. See the file-top race explanation. */
  resetCounter?: number;
  ariaLabel: string;
  placeholder: string;
  title?: string;
  /** Debounce window in ms. Defaults to 200 — the vocabulary shared by
   *  every tracking filter chip bar. */
  debounceMs?: number;
  wrapperClassName?: string;
  iconClassName?: string;
  inputClassName?: string;
  "data-testid"?: string;
}

const DEFAULT_WRAPPER_CLASS = "relative";

const DEFAULT_ICON_CLASS =
  "pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400";

const DEFAULT_INPUT_CLASS =
  "h-8 w-72 rounded-full border border-slate-300 bg-white pl-7 pr-3 text-caption text-slate-700 focus:border-brand-400 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200";

const DEFAULT_DEBOUNCE_MS = 200;

export default function DebouncedSearchInput({
  value,
  onDebouncedChange,
  resetCounter,
  ariaLabel,
  placeholder,
  title,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  wrapperClassName = DEFAULT_WRAPPER_CLASS,
  iconClassName = DEFAULT_ICON_CLASS,
  inputClassName = DEFAULT_INPUT_CLASS,
  "data-testid": dataTestId,
}: DebouncedSearchInputProps) {
  const debounceHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDebouncedChangeRef = useRef(onDebouncedChange);
  useEffect(() => {
    onDebouncedChangeRef.current = onDebouncedChange;
  });

  const [localValue, setLocalValue] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const [prevResetCounter, setPrevResetCounter] = useState(resetCounter);
  if (value !== prevValue || resetCounter !== prevResetCounter) {
    setPrevValue(value);
    setPrevResetCounter(resetCounter);
    setLocalValue(value);
  }

  // Cancel any in-flight debounce when the value or resetCounter changes so
  // a stale-typed value can't land after a parent-driven reset.
  useEffect(() => {
    if (debounceHandleRef.current) {
      clearTimeout(debounceHandleRef.current);
      debounceHandleRef.current = null;
    }
  }, [value, resetCounter]);
  useEffect(() => {
    return () => {
      if (debounceHandleRef.current) clearTimeout(debounceHandleRef.current);
    };
  }, []);

  const handleChange = (next: string) => {
    setLocalValue(next);
    if (debounceHandleRef.current) clearTimeout(debounceHandleRef.current);
    debounceHandleRef.current = setTimeout(() => {
      onDebouncedChangeRef.current(next);
    }, debounceMs);
  };

  return (
    <div className={wrapperClassName}>
      <Search aria-hidden="true" className={iconClassName} />
      <input
        type="search"
        aria-label={ariaLabel}
        placeholder={placeholder}
        title={title}
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        className={inputClassName}
        data-testid={dataTestId}
      />
    </div>
  );
}
